import { useHistory } from 'react-router';
import { ComponentSectionContextType } from '../../../types/context';
import { getFormActionUrlFromComponentSection } from '../../../utils/forms/getFormActionUrlFromComponentSection';
import useRoutes from '../../useRoutes';
import { useFormTokenChainContext } from '../../context/useFormTokenChainContext';
import {
  Dataset,
  DatasetControl,
  FormControlAction,
} from '../../../types/types';
import { FormApiMethodsRequestData } from '../../../api/types/types';
import { createRootTokenByFormType } from './createRootTokenByFormType';
import { getDataKey } from '../../../utils/getDataKey';
import { debugLogger } from '../../../debug/DebugLogger';

const log = debugLogger.scope('formActions');

export const navigateToForm = (
  componentSection: ComponentSectionContextType['componentSection'],
  routes: ReturnType<typeof useRoutes>,
  history: ReturnType<typeof useHistory>,
  dataset: Dataset,
  control: DatasetControl,
  rootDataset: Dataset,
  resolvedRowsToUse: any[],
  slaveTabIndex: number,
  fromPathname: string,
  fromItemExternalUrl: boolean | undefined,
  masterExpandedRow: any,
  getActualTokenFromUrl: ReturnType<
    typeof useFormTokenChainContext
  >['getActualTokenFromUrl'],
  createRootToken: ReturnType<
    typeof useFormTokenChainContext
  >['createRootToken'],
  createNestedToken: ReturnType<
    typeof useFormTokenChainContext
  >['createNestedToken'],
  token: string | null,
  referenceDatasetId?: string,
  itemId?: string,
  closeModal?: () => void
) => {
  closeModal?.();

  // get form action url
  const formActionUrl = getFormActionUrlFromComponentSection(
    componentSection,
    routes,
    control,
    rootDataset,
    resolvedRowsToUse,
    slaveTabIndex,
    fromPathname,
    masterExpandedRow,
    referenceDatasetId,
    itemId
  );

  // action url fallback
  if (formActionUrl === routes.notImplemented()) {
    history.push(routes.notImplemented());
    return;
  }

  /** @debugFeature formActions Logs the resolved form action url before navigation: section, form type and id, dataset and selected row count. */
  log('form action url resolved', () => ({
    formActionUrl,
    componentSection,
    formType: (control.action as FormControlAction).formType,
    formId: (control.action as FormControlAction).formId,
    datasetId: dataset.datasetId,
    rowCount: resolvedRowsToUse.length,
  }));

  const actualToken = getActualTokenFromUrl();
  const redirectToRootLevel = actualToken === null;
  const dataKey = getDataKey(dataset);
  const masterDataKey = getDataKey(rootDataset);

  // form root level flow
  if (redirectToRootLevel) {
    const redirectToSubset =
      formActionUrl.includes('subset') && formActionUrl.includes('form');
    const retypedControlAction = control.action as FormControlAction;

    const requestData: FormApiMethodsRequestData = {
      mode: retypedControlAction.multiSelectMode ?? 'sequential',
      formType: retypedControlAction.formType,
      datasetId: dataset.datasetId,
      formId: retypedControlAction.formId,
      sessionId: token ?? '',
      id:
        resolvedRowsToUse.length > 0
          ? resolvedRowsToUse[0][dataKey]
          : undefined,
      ids: resolvedRowsToUse.map((row) => row[dataKey]),
      idItem: dataKey,
      ...(redirectToSubset
        ? {
            master: {
              id: masterExpandedRow[masterDataKey],
              idItem: masterDataKey,
            },
          }
        : {}),
    };

    const rootToken = createRootTokenByFormType(
      formActionUrl,
      fromPathname,
      fromItemExternalUrl,
      control,
      // Pass ids only (already derived via getDataKey above) — keeps token meta small for localStorage
      requestData.ids,
      requestData,
      createRootToken
    );

    history.replace(rootToken.url, { rootToken });
    return;
  }

  // nested token form flow — build requestData from current nesting level
  const retypedControlAction = control.action as FormControlAction;

  const childRequestData: FormApiMethodsRequestData = {
    formType: actualToken.meta?.requestData?.['formType'],
    mode: actualToken.meta?.requestData?.['mode'],
    datasetId: dataset.datasetId,
    formId: retypedControlAction.formId,
    sessionId: token ?? '',
    id:
      resolvedRowsToUse.length > 0 ? resolvedRowsToUse[0][dataKey] : undefined,
    ids: resolvedRowsToUse.map((row) => row[dataKey]),
    idItem: dataKey,
    ...(actualToken.meta?.requestData?.['master']
      ? {
          master: {
            id: actualToken.meta?.requestData?.['master'].id,
            idItem: actualToken.meta?.requestData?.['master'].idItem,
          },
        }
      : {}),
  };

  const childToken = createNestedToken(actualToken.id, {
    url: formActionUrl,
    meta: {
      controlLabel: control.label,
      requestData: childRequestData,
      controlSelectionMode: control.selectionMode,
    },
  });

  history.replace(childToken.url, { childToken });
  return;
};
